/**
 * Blog System Types
 *
 * TypeScript interfaces for the XRGallery blog system.
 * Used by both build-time scripts and runtime rendering.
 */

// ============================================================================
// Article Types
// ============================================================================

/**
 * Article frontmatter metadata (from YAML in markdown files)
 */
export interface ArticleFrontmatter {
  title: string;
  slug: string;
  description: string;
  author: string;
  publishedAt: string;
  updatedAt?: string;
  category: CategorySlug;
  tags: string[];
  coverImage?: string;
  coverImageAlt?: string;
  readingTime?: number;
  seoKeywords?: string[];
  featured?: boolean;
  draft?: boolean;
}

/**
 * Full article with content
 */
export interface Article extends ArticleFrontmatter {
  content: string;
  htmlContent?: string;
}

/**
 * Article summary for listing pages (excludes full content)
 */
export interface ArticleSummary {
  title: string;
  slug: string;
  description: string;
  author: string;
  publishedAt: string;
  category: CategorySlug;
  tags: string[];
  coverImage?: string;
  coverImageAlt?: string;
  readingTime?: number;
  featured?: boolean;
}

// ============================================================================
// Category Types
// ============================================================================

/**
 * Valid category slugs
 */
export type CategorySlug = '360-photography' | 'tutorials' | 'industry';

/**
 * Category definition
 */
export interface Category {
  id: string;
  name: string;
  slug: CategorySlug;
  description: string;
  color: string;
}

/**
 * Categories configuration
 */
export interface CategoriesConfig {
  categories: Category[];
}

// ============================================================================
// Blog Manifest Types (Build-time generated)
// ============================================================================

/**
 * Blog manifest generated at build time
 * Contains all article metadata for the listing page
 */
export interface BlogManifest {
  generatedAt: string;
  articles: ArticleSummary[];
  categories: Category[];
  totalArticles: number;
}

// ============================================================================
// SEO Types
// ============================================================================

/**
 * SEO metadata for an article page
 */
export interface ArticleSEO {
  title: string;
  description: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogType: 'article';
  articlePublishedTime: string;
  articleModifiedTime: string;
  articleAuthor: string;
  articleSection: string;
  articleTags: string[];
  twitterCard: 'summary_large_image';
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
}

/**
 * JSON-LD Article schema
 */
export interface ArticleSchema {
  '@context': 'https://schema.org';
  '@type': 'Article';
  headline: string;
  description: string;
  image: string;
  author: {
    '@type': 'Person';
    name: string;
    url?: string;
  };
  publisher: {
    '@type': 'Organization';
    name: string;
    logo: {
      '@type': 'ImageObject';
      url: string;
    };
  };
  datePublished: string;
  dateModified: string;
  mainEntityOfPage: {
    '@type': 'WebPage';
    '@id': string;
  };
  keywords?: string;
  articleSection?: string;
}

// ============================================================================
// RSS Types
// ============================================================================

/**
 * RSS feed item
 */
export interface RSSItem {
  title: string;
  link: string;
  guid: string;
  description: string;
  pubDate: string;
  author: string;
  category: string;
  enclosure?: {
    url: string;
    type: string;
  };
}

/**
 * RSS feed configuration
 */
export interface RSSFeedConfig {
  title: string;
  link: string;
  description: string;
  language: string;
  lastBuildDate: string;
  items: RSSItem[];
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Pagination state
 */
export interface PaginationState {
  currentPage: number;
  totalPages: number;
  articlesPerPage: number;
  totalArticles: number;
}

/**
 * Filter state for blog listing
 */
export interface BlogFilterState {
  category: CategorySlug | 'all';
  page: number;
  searchQuery?: string;
}

// ============================================================================
// Constants
// ============================================================================

export const ARTICLES_PER_PAGE = 9;
export const BASE_URL = 'https://xrgallery.online';
export const BLOG_PATH = '/blog';

/**
 * Default categories configuration
 */
export const DEFAULT_CATEGORIES: Category[] = [
  {
    id: '360-photography',
    name: '360 Photography',
    slug: '360-photography',
    description: 'Tutorials and guides for capturing 360-degree panoramic images',
    color: '#FF6B6B'
  },
  {
    id: 'tutorials',
    name: 'Tutorials',
    slug: 'tutorials',
    description: 'Step-by-step guides for creating immersive virtual tours',
    color: '#4ECDC4'
  },
  {
    id: 'industry',
    name: 'Industry',
    slug: 'industry',
    description: 'Real-world applications in real estate, museums, and training',
    color: '#9B59B6'
  }
];
